import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface PerformanceMetrics {
  fps: number;
  memoryUsage?: number;
  loadTime: number;
  renderCount: number;
}

export const usePerformanceMonitor = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    loadTime: 0,
    renderCount: 0,
  });

  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animationFrameId: number;

    // FPS Counter
    const measureFPS = (currentTime: number) => {
      frameCount++;
      
      if (currentTime >= lastTime + 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        
        setMetrics(prev => ({
          ...prev,
          fps,
          renderCount: prev.renderCount + 1,
        }));

        frameCount = 0;
        lastTime = currentTime;
      }

      animationFrameId = requestAnimationFrame(measureFPS);
    };

    animationFrameId = requestAnimationFrame(measureFPS);

    // Memory Usage (if available)
    const measureMemory = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        const usedMemory = memory.usedJSHeapSize / (1024 * 1024); // Convert to MB
        
        setMetrics(prev => ({
          ...prev,
          memoryUsage: usedMemory,
        }));
      }
    };

    const memoryInterval = setInterval(measureMemory, 2000);

    // Load Time
    if (performance.timing) {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      setMetrics(prev => ({ ...prev, loadTime }));
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      clearInterval(memoryInterval);
    };
  }, []);

  return metrics;
};

interface PerformanceMonitorProps {
  enabled?: boolean;
}

export const PerformanceMonitor = ({ enabled = process.env.NODE_ENV === 'development' }: PerformanceMonitorProps) => {
  const metrics = usePerformanceMonitor();
  const [isVisible, setIsVisible] = useState(true);

  if (!enabled || !isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-lg p-3 text-xs font-mono shadow-lg">
      <div className="flex items-start justify-between mb-2 pb-2 border-b border-border">
        <span className="text-muted-foreground font-semibold">Performance</span>
        <button
          onClick={() => setIsVisible(false)}
          className="text-muted-foreground hover:text-foreground transition-colors -mt-1 -mr-1"
          aria-label="Close performance monitor"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">FPS:</span>
          <span className={metrics.fps < 30 ? "text-destructive" : metrics.fps < 50 ? "text-yellow-500" : "text-green-500"}>
            {metrics.fps}
          </span>
        </div>
        {metrics.memoryUsage && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Memory:</span>
            <span className="text-foreground">{metrics.memoryUsage.toFixed(1)} MB</span>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Load:</span>
          <span className="text-foreground">{(metrics.loadTime / 1000).toFixed(2)}s</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Renders:</span>
          <span className="text-foreground">{metrics.renderCount}</span>
        </div>
      </div>
    </div>
  );
};
